import { useCallback, useMemo, useState } from 'react'
import { Col, Row, Spinner } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'

import { useSaveAIAgentResultMutation } from '../../../../../store/integrations/integrations'
import { useLazyListAiAgentResultQuery } from '../../../../../store/ai/aiAgentResult'
import { useGetAllDocumentTypesQuery } from '../../../../../store/ai/documentTypes'
import { useLazyGetAllExternalUsersQuery } from '../../../../../store/externalUser/externalUser'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { useGetMeQuery } from '../../../../../store/user/user'

import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { ContactDropdown } from '../../../../MeetingSummary/CRM/ContactDropdown'

import { getExternalLink } from '../../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { SyncStatusView } from '../../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

import { AIAgentResult, AIAgentType } from '../../../../../models/AIAgent'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { TestResultDisplay } from '../TestResultDisplay'
import '../MappingTester.scss'

export interface DocumentSummaryMappingTesterProps {
  serviceId: IntegrationServiceId
}

export const DocumentSummaryMappingTester: React.FC<DocumentSummaryMappingTesterProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { data: account } = useGetAccountQuery({ accountId })
  const { data: allDocumentTypes } = useGetAllDocumentTypesQuery({ accountId })

  const {
    formState: { isDirty, isValid },
  } = useFormContext<IntegrationSettings>()
  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)

  const [selectedContactExternalId, setSelectedContactExternalId] = useState<string | null>(null)
  const [selectedContactInternalId, setSelectedContactInternalId] = useState<string | null>(null)
  const [selectedAiAgentResult, setSelectedAiAgentResult] = useState<AIAgentResult | null>(null)
  const [documentResultOptions, setDocumentResultOptions] = useState<SelectOption[]>([])
  const [aiAgentResultsMap, setAiAgentResultsMap] = useState<Record<string, AIAgentResult>>({})
  const [rawResult, setRawResult] = useState<unknown>(null)
  const [externalLink, setExternalLink] = useState<string | null>(null)

  const [triggerListAiAgentResults, { isLoading: isLoadingDocumentResults }] = useLazyListAiAgentResultQuery()
  const [triggerGetAllExternalUsers] = useLazyGetAllExternalUsersQuery()
  const [triggerSaveAIAgentResult, { isLoading: savingResult, isSuccess: saveSuccess, isError: saveIsError }] = useSaveAIAgentResultMutation()

  const contactObject = useMemo(() => {
    if (!account?.data) {
      return 'contact'
    }
    const config = getAccountCRMIntegrationConfig(account.data, serviceId)
    if (!config) {
      return 'contact'
    }
    const mapping = config.mappings[AccountIntegrationConfigMappingType.CONTACT]
    return typeof mapping === 'string' ? mapping : (mapping?.objectName ?? 'contact')
  }, [account, serviceId])

  const onContactOptionSelected = useCallback(
    (option: SelectOption) => {
      setSelectedContactExternalId(option.id)
      setSelectedAiAgentResult(null)
      setDocumentResultOptions([])
      setAiAgentResultsMap({})
      setRawResult(null)
      setExternalLink(null)
      setSelectedContactInternalId(null)

      const getResultsForContact = async () => {
        const { data: externalUsers } = await triggerGetAllExternalUsers({ accountId, serviceId, externalId: option.id })
        const internalId = externalUsers?.data?.[0]?.id ?? null
        setSelectedContactInternalId(internalId)

        if (!internalId) {
          return
        }

        const { data: results } = await triggerListAiAgentResults({
          accountId,
          type: AIAgentType.DOCUMENT_SUMMARY,
          resultKey: `${internalId}|`,
          partialKeyMatch: true,
        })

        if (!results?.length) {
          return
        }

        const resultMap: Record<string, AIAgentResult> = {}
        const options: SelectOption[] = results.map((r) => {
          resultMap[r.id] = r
          const keyParts = r.resultKey.split('|')
          const documentTypeId = keyParts[2]
          const documentTypeName = documentTypeId ? (allDocumentTypes?.data?.find((dt) => dt.id === documentTypeId)?.name ?? documentTypeId) : r.resultKey
          const date = new Date(r.createdAt).toLocaleDateString()
          return { id: r.id, label: `${documentTypeName} — ${date}` }
        })

        setAiAgentResultsMap(resultMap)
        setDocumentResultOptions(options)
        setSelectedAiAgentResult(resultMap[options[0].id])
      }

      getResultsForContact()
    },
    [accountId, serviceId, allDocumentTypes?.data, triggerGetAllExternalUsers, triggerListAiAgentResults],
  )

  const onDocumentResultSelected = useCallback(
    (option: SelectOption) => {
      setSelectedAiAgentResult(aiAgentResultsMap[option.id] ?? null)
      setRawResult(null)
      setExternalLink(null)
    },
    [aiAgentResultsMap],
  )

  const handleRunTest = useCallback(async () => {
    if (!selectedContactExternalId || !selectedAiAgentResult) {
      return
    }
    setRawResult(null)
    setExternalLink(null)

    try {
      const result = await triggerSaveAIAgentResult({
        accountId,
        connector: serviceId,
        body: {
          relatedTo: { id: selectedContactExternalId, type: contactObject },
          resultId: selectedAiAgentResult.id,
        },
      }).unwrap()

      setRawResult(result)
      const syncStatus = (result as { data?: SyncStatusView[] } | undefined)?.data?.[0]
      if (syncStatus && currentUser) {
        setExternalLink(getExternalLink({ syncStatus, currentUser }) || null)
      }
    } catch (err) {
      setRawResult(err)
    }
  }, [selectedContactExternalId, selectedAiAgentResult, accountId, serviceId, contactObject, currentUser, triggerSaveAIAgentResult])

  return (
    <>
      <Row className='mb-4 mt-2 align-items-center'>
        <Col>Contact</Col>
        <Col>
          <ContactDropdown serviceId={serviceId} onContactOptionSelected={onContactOptionSelected} />
        </Col>
      </Row>
      <Row className='mb-4 align-items-center'>
        <Col>Document</Col>
        <Col>
          <SelectDropdown
            options={documentResultOptions}
            initialSelectedOption={documentResultOptions.length > 0 ? documentResultOptions[0] : null}
            placeholder={
              isLoadingDocumentResults
                ? 'Loading…'
                : !selectedContactExternalId
                  ? 'Select a contact first'
                  : documentResultOptions.length === 0
                    ? 'No documents found for this contact'
                    : 'Select Document'
            }
            onOptionSelected={onDocumentResultSelected}
            loading={isLoadingDocumentResults}
            disabled={!selectedContactInternalId || isLoadingDocumentResults}
          />
        </Col>
      </Row>

      <div className='border border-dark rounded-3 p-4 mt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-sliders2 fs-5'></i>
              <h6 className='fw-bold mb-0'>Save Document Summary Test</h6>
            </div>
            <p className='text-secondary mb-0 small'>Verify that Document Summaries are saved to your {integrationName} as configured.</p>
          </div>
          <Tooltip id='document-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleRunTest}
              disabled={savingResult || !selectedContactExternalId || !selectedContactInternalId || !selectedAiAgentResult || isFormUnsaved || !currentUser}
            >
              {savingResult ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>

        <TestResultDisplay
          isLoading={savingResult}
          loadingText='Saving Document Summary'
          hasResult={saveSuccess || saveIsError}
          isSuccess={saveSuccess}
          isError={saveIsError}
          integrationName={integrationName}
          rawResult={rawResult}
          successContent={
            <>
              <p className='mb-3 text-dark ps-4'>The document summary was saved to your {integrationName} as configured</p>
              <div className='ps-4'>
                {externalLink ? (
                  <a href={externalLink} target='_blank' rel='noreferrer' className='btn btn-outline-dark btn-sm'>
                    View in {integrationName}
                  </a>
                ) : (
                  <button className='btn btn-outline-dark btn-sm' disabled>
                    View in {integrationName}
                  </button>
                )}
              </div>
            </>
          }
          errorContent={<p className='mb-3 text-dark ps-4'>Failed to export the document to {integrationName}. Please verify your integration settings.</p>}
        />
      </div>
    </>
  )
}
